import { useCallback } from "react";
import { usePreferences } from "@src/packages/preferences";
import { useLocale } from "@src/packages/i18n/useLocale";
import {
  formatDate as formatLocalizedDate,
  type DateInput,
} from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";

export type DateFormat = "MM/DD/YYYY" | "DD/MM/YYYY" | "YYYY-MM-DD";

/** Numeric date layout to default to when the user hasn't picked one. */
function defaultDateFormatFor(locale: Locale): DateFormat {
  return locale === "fr" ? "DD/MM/YYYY" : "MM/DD/YYYY";
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Placeholder text matching the typing shape for each layout. */
export function placeholderFor(format: DateFormat): string {
  return format;
}

/**
 * Auto-format raw user input into the user-preferred date display while
 * typing. Strips non-digits, caps at 8 digits, inserts the right separator
 * at the right positions.
 */
export function formatDateTyping(
  input: string,
  format: DateFormat,
  prev?: string,
): string {
  const digits = input.replace(/\D/g, "").slice(0, 8);
  const prevDigits = prev ? prev.replace(/\D/g, "").length : 0;
  const grew = digits.length > prevDigits;

  if (format === "YYYY-MM-DD") {
    const s = "-";
    if (digits.length < 4) return digits;
    if (digits.length === 4) return grew ? `${digits}${s}` : digits;
    if (digits.length < 6) {
      return `${digits.slice(0, 4)}${s}${digits.slice(4)}`;
    }
    if (digits.length === 6) {
      const base = `${digits.slice(0, 4)}${s}${digits.slice(4)}`;
      return grew ? `${base}${s}` : base;
    }
    return `${digits.slice(0, 4)}${s}${digits.slice(4, 6)}${s}${digits.slice(6, 8)}`;
  }
  // MM/DD/YYYY and DD/MM/YYYY share the 2-2-4 shape
  const s = "/";
  if (digits.length < 2) return digits;
  if (digits.length === 2) return grew ? `${digits}${s}` : digits;
  if (digits.length < 4) {
    return `${digits.slice(0, 2)}${s}${digits.slice(2)}`;
  }
  if (digits.length === 4) {
    const base = `${digits.slice(0, 2)}${s}${digits.slice(2)}`;
    return grew ? `${base}${s}` : base;
  }
  return `${digits.slice(0, 2)}${s}${digits.slice(2, 4)}${s}${digits.slice(4, 8)}`;
}

/**
 * Parse a fully-typed display string (with separators) back to canonical
 * ISO YYYY-MM-DD. Returns "" if the input is incomplete or doesn't
 * resolve to a real date (e.g. 02/30/2026 isn't a real day).
 */
export function parseTypedToIso(display: string, format: DateFormat): string {
  const digits = display.replace(/\D/g, "");
  if (digits.length !== 8) return "";

  let year: string;
  let month: string;
  let day: string;
  if (format === "YYYY-MM-DD") {
    year = digits.slice(0, 4);
    month = digits.slice(4, 6);
    day = digits.slice(6, 8);
  } else if (format === "DD/MM/YYYY") {
    day = digits.slice(0, 2);
    month = digits.slice(2, 4);
    year = digits.slice(4, 8);
  } else {
    // MM/DD/YYYY
    month = digits.slice(0, 2);
    day = digits.slice(2, 4);
    year = digits.slice(4, 8);
  }

  const date = new Date(`${year}-${month}-${day}T00:00:00`);
  if (isNaN(date.getTime())) return "";
  if (date.getMonth() + 1 !== parseInt(month, 10)) return "";
  if (date.getDate() !== parseInt(day, 10)) return "";
  return `${year}-${month}-${day}`;
}

/** Convert canonical ISO YYYY-MM-DD into the user's preferred display. */
export function isoToDisplay(iso: string, format: DateFormat): string {
  if (!iso || iso.length !== 10) return "";
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return "";
  if (format === "YYYY-MM-DD") return `${year}-${month}-${day}`;
  if (format === "DD/MM/YYYY") return `${day}/${month}/${year}`;
  return `${month}/${day}/${year}`;
}

function parseDate(input: string | Date): Date {
  if (input instanceof Date) return input;
  if (DATE_ONLY_RE.test(input)) return new Date(input + "T00:00:00");
  return new Date(input);
}

export function formatDate(
  input: string | Date | null | undefined,
  format: DateFormat,
): string {
  if (!input) return "";

  const d = parseDate(input);
  if (isNaN(d.getTime())) return "";

  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const year = String(d.getFullYear());

  switch (format) {
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    case "MM/DD/YYYY":
    default:
      return `${month}/${day}/${year}`;
  }
}

export function formatDateAbbrev(
  input: string | Date | null | undefined,
  locale: Locale = "en",
): string {
  if (!input) return "";
  return formatLocalizedDate(input as DateInput, locale, {
    month: "short",
    day: "numeric",
  });
}

export function formatDateLong(
  input: string | Date | null | undefined,
  locale: Locale = "en",
): string {
  if (!input) return "";
  return formatLocalizedDate(input as DateInput, locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function useDateFormat() {
  const { data } = usePreferences();
  const { locale } = useLocale();
  // Respect an explicit user preference; otherwise default to the locale's
  // conventional numeric layout (fr → DD/MM/YYYY) instead of always US.
  const fmt = (data?.dateFormat ?? defaultDateFormatFor(locale)) as DateFormat;

  const format = useCallback(
    (input: string | Date | null | undefined) => formatDate(input, fmt),
    [fmt],
  );

  const abbrev = useCallback(
    (input: string | Date | null | undefined) => formatDateAbbrev(input, locale),
    [locale],
  );

  const long = useCallback(
    (input: string | Date | null | undefined) => formatDateLong(input, locale),
    [locale],
  );

  return { format, abbrev, long, dateFormat: fmt };
}
